import React, { useMemo, useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Card,
  Button,
  Dialog,
  Paper,
  Box,
} from "@mui/material";
import { FieldArray, FormikProvider, useFormik } from "formik";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import CloseIcon from "@mui/icons-material/Close";
import {
  useCreateGradeMutation,
  useGetallSubjectQuery,
  useGetAllGradeCountQuery,
} from "../../../Api/attoDeskApi";
import { IGrade, ISubject } from "../../../Api/Interface/api.interface";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import LoadingDialog from "../../../Components/LoadingDialog/LoadingDialog";

interface ITeampopup {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: IGrade;
}

const ItemList = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1, 2),
  borderRadius: 8,
  cursor: "pointer",
  transition: "all 0.2s ease",
  boxShadow: "none",

  backgroundColor: selected
    ? theme.palette.primary.main
    : theme.palette.background.paper,

  color: selected ? "#ffffff" : theme.palette.text.primary,

  "& .MuiTypography-root": {
    color: selected ? "#ffffff" : theme.palette.text.primary,
  },

  "&:hover": {
    // boxShadow: theme.shadows[3],
    backgroundColor: selected
      ? theme.palette.primary.dark
      : theme.palette.action.hover,
  },
}));

const Team = ({ openModel = false, handleCloseDialog, data }: ITeampopup) => {
  const classes = useStyles();
  const [open] = React.useState(openModel);
  const theme = useTheme();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  const [newCompany, { isLoading }] = useCreateGradeMutation();
  const isMobileRes = useIsMobile();

  const {
    data: gradeData,
    isLoading: subjectIsLoading,
    isError,
    refetch,
  } = useGetallSubjectQuery({
    searchText: "",
  });

  const {
    data: countData,
    isLoading: countDataIsLoading,
    refetch: countRefetch,
    isError: countIsError,
  } = useGetAllGradeCountQuery({
    searchText: "",
  });

  const handleClose = () => {
    handleCloseDialog(false);
  };

  useEffect(() => {
    if (data?.subject_ids) {
      setSelectedCategories(data.subject_ids);
    }
  }, [data]);

  const gradeList: ISubject[] = Array.isArray(gradeData?.data)
    ? (gradeData?.data as ISubject[])
    : [];

  const nextOrderNumber = Number(countData?.data || 0) + 1;
  const formik = useFormik<IGrade>({
    initialValues: {
      id: Number(data?.id),
      grade: data?.grade || "",
      subject_ids: data?.subject_ids || [],
      order_number: nextOrderNumber,
      notes: data?.notes || "",
    },
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {
      try {
        const updatedData = {
          ...(data?.id ? { id: Number(values.id) } : {}),
          grade: toProperCase(values.grade),
          subject_ids: selectedCategories,
          order_number: values.order_number,
          notes: values.notes,
        };

        const response = await newCompany(updatedData).unwrap();

        if (!response.status) {
          showErrorMessage(response.message);
        } else {
          showMessage(response.message);
          handleClose();
        }
      } catch (error) {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const handleCategoryChange = (categoryId: number) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const formValid = useMemo(() => {
    return formik.values.grade === "" || formik.values.grade === undefined
      ? false
      : true;
  }, [formik]);

  if (subjectIsLoading || countDataIsLoading)
    return (
      <LoadingDialog
        open={subjectIsLoading || countDataIsLoading}
        title="Loading Data"
        message="Fetching subjects, please wait..."
      />
    );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-describedby="alert-dialog-description"
      classes={{ paper: "inherit" }}
      fullScreen={isMobileRes}
      fullWidth={isMobileRes}
      maxWidth="md"
    >
      <Grid className={isMobileRes ? classes.DialogMobile : classes.Dialog}>
        <Grid container spacing={2}>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Grid
              container
              justifyContent={"space-between"}
              spacing={5}
              sx={{ padding: 2 }}
              className={classes.titleGrid}
            >
              <Grid item pt={2} alignContent={"center"}>
                <Typography className={classes.cardTitle}>
                  {data ? "Edit Grade " : "Create Grade"}
                </Typography>
              </Grid>
              <Grid
                item
                pt={2}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  className={classes.editButton}
                  onClick={handleClose}
                >
                  <CloseIcon />
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <FormikProvider value={formik}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={2}>
                        <Grid item lg={12} md={12} sm={6} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Grade
                                <span className={classes.requiedfield}>*</span>
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Grade"
                                size="small"
                                {...formik.getFieldProps("grade")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>{" "}
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={2}>
                        <Grid item lg={12} md={12} sm={6} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Order Number
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder=" Order Number"
                                size="small"
                                {...formik.getFieldProps("order_number")}
                                className={classes.textField}
                                type="number"
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>{" "}
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={2}>
                        <Grid item lg={12} md={12} sm={6} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">Note</Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TextField
                                placeholder="Note"
                                size="small"
                                multiline
                                minRows={3}
                                {...formik.getFieldProps("notes")}
                                className={classes.textField}
                                InputProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                                InputLabelProps={{
                                  sx: {
                                    fontSize: 14,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Grid>{" "}
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Card className={classes.card}>
                      <Grid container spacing={3} p={2}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                          <Grid container alignItems="center">
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Typography variant="subtitle1">
                                Select Subjects
                              </Typography>
                            </Grid>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <Grid container spacing={1}>
                                {gradeList.map((item: ISubject) => {
                                  const isSelected =
                                    selectedCategories.includes(item.id);

                                  return (
                                    <Grid item key={item.id}>
                                      <ItemList
                                        selected={isSelected}
                                        onClick={() =>
                                          handleCategoryChange(item.id)
                                        }
                                        sx={{
                                          background: isSelected
                                            ? theme.palette.primary.main
                                            : "#eef2f7",

                                          color: isSelected
                                            ? "#ffffff"
                                            : theme.palette.primary.main,
                                        }}
                                      >
                                        <Typography variant="body2">
                                          {item.subject_name || "Undefined"}
                                        </Typography>
                                      </ItemList>
                                    </Grid>
                                  );
                                })}
                              </Grid>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Card>
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <Grid container spacing={1}>
                      <Grid item>
                        <Button
                          variant="contained"
                          className={classes.addButton}
                          onClick={() => formik.handleSubmit()}
                          disabled={!formValid || isLoading}
                        >
                          {isLoading && (
                            <CircularProgress
                              size={18}
                              className={classes.isLoading}
                              sx={{ marginRight: 1 }}
                            />
                          )}
                          {data ? "Update" : "Create"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </form>
            </FormikProvider>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
};

export default Team;
